import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "/api/v1";

const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  me: () => api.get("/auth/me"),
};

export const modulesAPI = {
  getPaths: () => api.get("/modules/paths"),
  getAllPaths: () => api.get("/modules/all-paths"),
  getPath: (id) => api.get("/modules/paths/" + id),
  createPath: (data) => api.post("/modules/paths", data),
  getModules: (pathId) => api.get("/modules?path_id=" + pathId),
  getModule: (id) => api.get("/modules/" + id),
  createModule: (data) => api.post("/modules", data),
  addVideo: (moduleId, data) => api.post("/modules/" + moduleId + "/video", data),
  addTranscript: (moduleId, data) => api.post("/modules/" + moduleId + "/transcript", data),
  publishModule: (moduleId) => api.patch("/modules/" + moduleId + "/publish"),
  deleteModule: (moduleId) => api.delete("/modules/" + moduleId),
  deletePath: (pathId) => api.delete("/modules/paths/" + pathId),
  getCertifiedCount: (pathId) => api.get("/modules/paths/" + pathId + "/certified-agents-count"),
  enrolAgent: (data) => api.post("/modules/enrol", data),
  updateModule: (moduleId, data) => api.put("/modules/" + moduleId, data),
  updateVideo: (moduleId, data) => api.put("/modules/" + moduleId + "/video", data),
  updateTranscript: (moduleId, data) => api.put("/modules/" + moduleId + "/transcript", data),
};

export const questionsAPI = {
  startExam: (templateId) => api.post("/questions/exam/start/" + templateId),
  submitExam: (attemptId, answers) => api.post("/questions/exam/submit/" + attemptId, { answers }),
  getTemplates: (moduleId) => api.get("/questions/templates/" + moduleId),
  createTemplate: (data) => api.post("/questions/templates", data),
  updateTemplate: (id, data) => api.put("/questions/templates/" + id, data),
  getTopics: (moduleId) => api.get("/questions/topics/" + moduleId),
  createTopic: (data) => api.post("/questions/topics", data),
  getQuestions: (moduleId) => api.get("/questions/" + moduleId),
  createQuestion: (data) => api.post("/questions", data),
  updateQuestion: (questionId, data) => api.put("/questions/" + questionId, data),
  checkCooldown: (templateId) => api.get("/questions/check-cooldown/" + templateId),
  uploadTranscriptFile: (moduleId, formData) => api.post("/upload/transcript/" + moduleId, formData, {
    headers: { "Content-Type": "multipart/form-data" }
  }),
};

export const progressAPI = {
  getMyProgress: (pathId) => api.get("/progress/my-progress/" + pathId),
  getModuleProgress: (moduleId) => api.get("/progress/module/" + moduleId),
  getReadiness: (pathId) => api.get("/progress/readiness/" + pathId),
  trackVideo: (moduleId, percent, seconds) =>
    api.post("/progress/video", { module_id: moduleId, percent_watched: percent, watch_duration_seconds: seconds }),
  markTranscript: (moduleId) => api.post("/progress/transcript/" + moduleId),
  getTeamDashboard: (teamId) => api.get("/progress/team/" + teamId),
};

export const usersAPI = {
  getTeams: () => api.get("/users/teams"),
  bulkCreateUsers: (data) => api.post("/users/bulk-create", data),
  updateUser: (id, data) => api.put("/users/" + id, data),
  createTeam: (data) => api.post("/users/teams", data),
  deleteTeam: (id) => api.delete("/users/teams/" + id),
  getUsers: () => api.get("/users"),
  createUser: (data) => api.post("/users", data),
  deactivateUser: (userId) => api.patch("/users/" + userId + "/deactivate"),
  changePassword: (data) => api.post("/users/change-password", data),
  getTeams: () => api.get("/users/all-teams"),
  resetPassword: (userId) => api.patch("/users/admin/reset-password/" + userId),
};

export const reportsAPI = {
  getActivityLog: (limit = 100) => api.get("/reports/activity-log-db?limit=" + limit),
  getAgentAttempts: (userId) => api.get("/reports/agent/" + userId + "/attempts"),
  getAllAgentsProgress: () => api.get("/reports/all-agents"),
  getQuestionAnalysis: () => api.get("/reports/question-analysis"),
  getAnalyticsDashboard: () => api.get("/reports/analytics-dashboard"),
};

export default api;